/**
 * 自动预算二期 · **模板 + 按系列关联/开关** 的类型与校验（2026-10-07 · 需求 D / D-1）。
 *
 * ## 两张用户报障对应的根因形态（本模块用**类型 + 校验**堵死其中可用类型表达的部分）
 *
 *  1. 「把示范模板当成全局模板」⇒ `DEFAULT_AUTO_BUDGET_TEMPLATE` **只能作新建模板时的初值**：
 *     见 `autoBudgetTemplateInitialConfig()` 的注释；本模块**不提供**任何
 *     "取默认生效模板"的函数（那种函数一旦存在，就会被当成兜底用）。
 *  2. 「似乎全部打开了」⇒ 关联表的 `enabled` **默认 false**，且 ⭐
 *     `enabled === true` **必须**带非空 `templateId` ⇒ **"开启但没有模板"在类型/校验层就被拒绝**
 *     （库层另有同口径 CHECK：`enabled = false OR template_id IS NOT NULL`）。
 *
 * ## 与既有模块的关系
 *  · **不改** `auto-budget.ts` 的任何既有语义（`DEFAULT_AUTO_BUDGET_TEMPLATE` /
 *    `AUTO_BUDGET_SKIP_REASONS` / `evaluateAutoBudgetDecision` 原样）；
 *  · 本模块只**复用** `AutoBudgetTemplateConfig` 与 `validateAutoBudgetTemplateConfig`；
 *  · 零运行时依赖（只 import 本包内文件）。
 */

import {
  AUTO_BUDGET_CONFIG_VERSION,
  AUTO_BUDGET_ROAS_TIERS,
  validateAutoBudgetTemplateConfig,
  type AutoBudgetRoasTier,
  type AutoBudgetTemplateConfig,
  type AutoBudgetTemplateRejection,
} from "./auto-budget.js";

/** 模板 id 前缀（API 生成，形如 `abt_<random>`）。 */
export const AUTO_BUDGET_TEMPLATE_ID_PREFIX = "abt_";
/** 模板名长度上限（与自动启停模板同级，避免 UI 溢出）。 */
export const AUTO_BUDGET_TEMPLATE_NAME_MAX_LENGTH = 60;

/**
 * **示范模板**（用户 2026-10-07 给的那套；值 = 设计稿 §6 截图 + §4 默认阈值）。
 *
 * ⚠️ 它**只是"新建模板时的初始值"**（见 `autoBudgetTemplateInitialConfig()`），
 * **不是**任何对象的生效模板：没有"取默认生效模板"的出口，关联表 `enabled=true` 也强制要求
 * 非空 `templateId`（库层 CHECK 同口径）。
 */
export const AUTO_BUDGET_TEMPLATE_INITIAL_CONFIG: AutoBudgetTemplateConfig = {
  configVersion: AUTO_BUDGET_CONFIG_VERSION,
  budgetMinCents: 500, // 预算MINI $5（用户截图）
  budgetMaxCents: 5_000, // 预算MAX $50（用户截图）
  minSpend30dCents: 5_000, // 最小 30 天花费 $50（用户截图）
  maxAdjustIntervalDays: 2, // 同一预算层最多 2 天调整 1 次（用户口径）
  // ⭐ 内置默认阶梯 = 用户 11 档（`<50%` 档的 `fixed.cents` = 500 = 上面的 MIN）。
  tiers: AUTO_BUDGET_ROAS_TIERS.map((tier) => ({ ...tier, action: { ...tier.action } })),
};

/** 一个自动预算模板（对应 `ad_auto_budget_templates` 一行）。 */
export interface AutoBudgetTemplate {
  readonly templateId: string;
  readonly shopId: string;
  readonly name: string;
  readonly config: AutoBudgetTemplateConfig;
  readonly createdBy: string;
  readonly updatedBy: string;
  readonly createdAtIso: string;
  readonly updatedAtIso: string;
  /** 软删时刻；`null` = 生效中。 */
  readonly deletedAtIso: string | null;
}

/**
 * ⭐ **新建模板时的初值**（= 用户给你的示范模板）。
 *
 * ⚠️ **它只是"新建模板时的初始值"，绝不是"生效兜底"**：
 *  · 未关联模板、或关联了但未开启的系列 ⇒ **不参与预演**（`unassigned` / `enabled=false`），
 *    任何路径都**不得**用本值去预演/执行一个没有显式关联模板的系列；
 *  · 该注释是防复发的第一道墙 —— **不要**写 `orElseDefault()` 这类取兜底的函数。
 */
export function autoBudgetTemplateInitialConfig(): AutoBudgetTemplateConfig {
  // ⚠️ **必须深拷贝 `tiers`**：浅拷贝会让调用方改档位时**污染常量**（2026-10-09 由 spec 抓出；
  //    本函数的契约就是"改返回值不影响 `AUTO_BUDGET_TEMPLATE_INITIAL_CONFIG`"）。
  return {
    ...AUTO_BUDGET_TEMPLATE_INITIAL_CONFIG,
    tiers: AUTO_BUDGET_TEMPLATE_INITIAL_CONFIG.tiers.map((tier) => ({
      ...tier,
      action: { ...tier.action },
    })),
  };
}

/** 模板 id 形状校验：`abt_` + 4..64 位 `[A-Za-z0-9_-]`；非法 → `null`（不静默修正）。 */
export function parseAutoBudgetTemplateId(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const trimmed = raw.trim();
  return /^abt_[A-Za-z0-9_-]{4,64}$/.test(trimmed) ? trimmed : null;
}

/** 非空文本（trim 后非空、无控制字符、长度上限）；失败给契约同形的 rejection。 */
function parseText(
  raw: unknown,
  field: string,
  maxLength: number,
): { readonly ok: true; readonly value: string } | { readonly ok: false; readonly rejection: AutoBudgetTemplateRejection } {
  if (typeof raw !== "string") {
    return { ok: false, rejection: { field, code: "not_a_string", message: `${field} 必须是字符串` } };
  }
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { ok: false, rejection: { field, code: "empty", message: `${field} 不能为空` } };
  }
  if (trimmed.length > maxLength) {
    return {
      ok: false,
      rejection: { field, code: "too_long", message: `${field} 不能超过 ${String(maxLength)} 个字符` },
    };
  }
  if (/[\u0000-\u001f\u007f]/.test(trimmed)) {
    return { ok: false, rejection: { field, code: "control_char", message: `${field} 含控制字符` } };
  }
  return { ok: true, value: trimmed };
}

/** 模板配置里可由调用方覆盖的整数字段（与 `AutoBudgetTemplateConfig` 一一对应）。 */
const CONFIG_INT_FIELDS = [
  "configVersion",
  "budgetMinCents",
  "budgetMaxCents",
  "minSpend30dCents",
  "maxAdjustIntervalDays",
] as const;

/** ⭐ 阶梯是**数组**（结构由 `validateAutoBudgetTemplateConfig` 逐档校验）⇒ 单独放行。 */
const CONFIG_ARRAY_FIELDS = ["tiers"] as const;

export interface AutoBudgetTemplateWriteValue {
  readonly name: string;
  readonly config: AutoBudgetTemplateConfig;
}

export type AutoBudgetTemplateWriteParse =
  | { readonly ok: true; readonly value: AutoBudgetTemplateWriteValue }
  | { readonly ok: false; readonly rejections: readonly AutoBudgetTemplateRejection[] };

/**
 * 校验「新建 / 更新模板」的输入。
 *
 *  · `name`：trim、非空、≤ 60 字、无控制字符；
 *  · `config`：**允许部分字段**（缺省字段取 `autoBudgetTemplateInitialConfig()` 的初值）；
 *    给了的字段必须是安全整数，最后逐条过契约 `validateAutoBudgetTemplateConfig`
 *    （`budgetMinCents > 0`、`budgetMaxCents ≥ budgetMinCents`、`maxAdjustIntervalDays ≥ 1`、阶梯无洞无重叠 …）。
 *    **非法一律拒绝**（返回逐条 rejection），**不静默修正**。
 */
export function validateAutoBudgetTemplateWriteInput(input: {
  readonly name: unknown;
  readonly config: unknown;
}): AutoBudgetTemplateWriteParse {
  const rejections: AutoBudgetTemplateRejection[] = [];

  const name = parseText(input.name, "name", AUTO_BUDGET_TEMPLATE_NAME_MAX_LENGTH);
  if (!name.ok) rejections.push(name.rejection);

  const base = autoBudgetTemplateInitialConfig();
  const candidate: Record<string, unknown> = { ...base };
  if (input.config !== undefined && (typeof input.config !== "object" || input.config === null || Array.isArray(input.config))) {
    rejections.push({ field: "config", code: "not_an_object", message: "config 必须是 JSON 对象" });
  } else if (input.config !== undefined && typeof input.config === "object" && input.config !== null) {
    const provided = input.config as Record<string, unknown>;
    for (const key of Object.keys(provided)) {
      const known =
        (CONFIG_INT_FIELDS as readonly string[]).includes(key) ||
        (CONFIG_ARRAY_FIELDS as readonly string[]).includes(key);
      if (!known) {
        rejections.push({ field: `config.${key}`, code: "unknown_field", message: `未知的模板字段：${key}` });
      }
    }
    for (const field of CONFIG_INT_FIELDS) {
      const value = provided[field];
      if (value === undefined) continue;
      if (typeof value !== "number" || !Number.isSafeInteger(value)) {
        rejections.push({
          field,
          code: "not_integer",
          message: `${field} 必须是整数（分/ppm），实际：${String(value)}`,
        });
        continue;
      }
      candidate[field] = value;
    }
    // ⭐ `tiers`：只做"是数组"的粗筛，逐档细节交给 `validateAutoBudgetTemplateConfig`（唯一实现）。
    const tiers = provided["tiers"];
    if (tiers !== undefined) {
      if (!Array.isArray(tiers)) {
        rejections.push({ field: "config.tiers", code: "not_an_array", message: "tiers 必须是数组" });
      } else {
        candidate["tiers"] = tiers as readonly AutoBudgetRoasTier[];
      }
    }
  }

  if (rejections.length === 0) {
    const validation = validateAutoBudgetTemplateConfig(candidate as unknown as AutoBudgetTemplateConfig);
    rejections.push(...validation.rejections);
  }
  if (rejections.length > 0) return { ok: false, rejections };
  return {
    ok: true,
    value: { name: (name as { ok: true; value: string }).value, config: candidate as unknown as AutoBudgetTemplateConfig },
  };
}

// ---------------------------------------------------------------------------
// 系列 ↔ 模板 的关联 + 启用状态（`campaign_auto_budget`）
// ---------------------------------------------------------------------------

/** 一个系列的自动预算绑定（对应 `campaign_auto_budget` 一行）。 */
export interface AutoBudgetCampaignBinding {
  readonly shopId: string;
  readonly campaignId: string;
  /** `null` = **未关联**（与"关联了但关闭"可区分）。 */
  readonly templateId: string | null;
  /** ⭐ **默认关闭**；`true` 时必须带非空 `templateId`。 */
  readonly enabled: boolean;
  /** 开启时刻；关闭时保留最后一次开启时刻（`null` = 从未开启）。 */
  readonly enabledAtIso: string | null;
  readonly updatedAtIso: string;
  readonly updatedBy: string;
}

export interface AutoBudgetCampaignBindingValue {
  readonly shopId: string;
  readonly campaignId: string;
  readonly templateId: string | null;
  readonly enabled: boolean;
}

export type AutoBudgetCampaignBindingParse =
  | { readonly ok: true; readonly value: AutoBudgetCampaignBindingValue }
  | { readonly ok: false; readonly rejections: readonly AutoBudgetTemplateRejection[] };

/**
 * 校验「关联 / 解绑 / 开关」的输入。
 *
 * ⭐ 硬规则：`enabled === true` ⇒ **必须带非空合法 `templateId`**（`enabled_without_template`）
 * —— 这是"示范模板不得当兜底"在**输入侧**的落点，与库层 CHECK 同口径。
 */
export function validateAutoBudgetCampaignBinding(input: {
  readonly shopId: unknown;
  readonly campaignId: unknown;
  readonly templateId: unknown;
  readonly enabled: unknown;
}): AutoBudgetCampaignBindingParse {
  const rejections: AutoBudgetTemplateRejection[] = [];

  const shopId = parseText(input.shopId, "shopId", 120);
  if (!shopId.ok) rejections.push(shopId.rejection);
  const campaignId = parseText(input.campaignId, "campaignId", 120);
  if (!campaignId.ok) rejections.push(campaignId.rejection);

  if (typeof input.enabled !== "boolean") {
    rejections.push({ field: "enabled", code: "not_a_boolean", message: "enabled 必须是布尔值" });
  }
  const enabled = input.enabled === true;

  let templateId: string | null = null;
  if (input.templateId !== undefined && input.templateId !== null) {
    const parsed = parseAutoBudgetTemplateId(input.templateId);
    if (parsed === null) {
      rejections.push({
        field: "templateId",
        code: "invalid_template_id",
        message: "templateId 形状非法（应为 `abt_…`）",
      });
    } else {
      templateId = parsed;
    }
  }
  if (enabled && templateId === null && !rejections.some((entry) => entry.field === "templateId")) {
    rejections.push({
      field: "templateId",
      code: "enabled_without_template",
      message: "开启自动预算必须同时关联一个模板（示范模板不能作兜底）",
    });
  }

  if (rejections.length > 0) return { ok: false, rejections };
  return {
    ok: true,
    value: {
      shopId: (shopId as { ok: true; value: string }).value,
      campaignId: (campaignId as { ok: true; value: string }).value,
      templateId,
      enabled,
    },
  };
}

// ---------------------------------------------------------------------------
// preview 的「模板来源可见」（D-1 裁定：三态 + 行级 templateId/enabled）
// ---------------------------------------------------------------------------

/** 预演时该系列**实际依据**的模板来源。 */
export type AutoBudgetPreviewTemplateSource = "campaign_binding" | "request_override" | "unbound";

/** 三态的中文口径说明（响应直接带出，前端不另写一套）。 */
export const AUTO_BUDGET_TEMPLATE_SOURCE_NOTES: Readonly<Record<AutoBudgetPreviewTemplateSource, string>> = {
  campaign_binding: "本次使用该系列**关联的模板**（按系列指派）。",
  request_override: "本次使用你**临时指定的参数**试算，**不是**该系列关联的模板（纯预演，不会执行、不会开启任何东西）。",
  unbound: "该系列**未关联模板或未开启** ⇒ **不参与预演**（默认关闭；绝不套用示范模板）。",
};

export interface AutoBudgetPreviewTemplateDisclosure {
  readonly source: AutoBudgetPreviewTemplateSource;
  readonly templateId: string | null;
  readonly enabled: boolean;
  /** 中文一句话（`AUTO_BUDGET_TEMPLATE_SOURCE_NOTES` 的取值）。 */
  readonly note: string;
}

/** 构造行级披露（`enabled=false` 一律 `unbound`，**绝不**退回示范模板）。 */
export function describeAutoBudgetTemplateSource(input: {
  readonly enabled: boolean;
  readonly templateId: string | null;
  readonly overrideRequested: boolean;
}): AutoBudgetPreviewTemplateDisclosure {
  const source: AutoBudgetPreviewTemplateSource = !input.enabled
    ? "unbound"
    : input.overrideRequested
      ? "request_override"
      : "campaign_binding";
  return {
    source,
    templateId: input.templateId,
    enabled: input.enabled,
    note: AUTO_BUDGET_TEMPLATE_SOURCE_NOTES[source],
  };
}

/** 汇总里"被排除（未关联 / 未开启）"的计数（默认关闭在**汇总层**也要可见）。 */
export function countDisabledAutoBudgetCampaigns(
  states: readonly { readonly enabled: boolean }[],
): number {
  return states.filter((state) => !state.enabled).length;
}
